import React, { useContext, useEffect, useRef } from 'react';
import {
    AccordionProps,
    AccordionVariant,
    GenericAccordionProps,
} from '@primitives/Accordion/types';
import cx from 'classnames';
import styles from './Accordion.module.scss';
import { ArrowDownIcon } from '@icons';
import { AccordionGroupContext } from './constants';

export const Accordion: React.FC<AccordionProps> = ({
    containerClassName,
    expanded,
    variant,
    headerArrowClassName,
    headerArrowWrapperClassName,
    headerClassName,
    bodyClassName,
    header,
    children,
    onToggledExpansion,
    chevronIconProps = {},
}) => {
    const [isExpanded, setIsExpanded] = React.useState<boolean>(!!expanded);
    const didMountRef = useRef(false);

    // emit callback when isExpanded changes.
    useEffect(() => {
        // Adding a check of didMountRef to avoid this being called on first render itself
        if (onToggledExpansion && didMountRef.current) {
            onToggledExpansion(isExpanded);
        }
    }, [isExpanded]);

    // setIsExpanded is set equal to 'prop.expanded'
    useEffect(() => {
        setIsExpanded(!!expanded);
    }, [expanded]);

    useEffect(() => {
        if (didMountRef.current) return;
        didMountRef.current = true;
    }, []);

    const accordionGroupContext: GenericAccordionProps = useContext(AccordionGroupContext);

    const accordionContainerClassName = cx(
        styles['accordion'],
        (variant || accordionGroupContext.variant) === AccordionVariant.UNDERLINED
            ? styles['accordion-underlined']
            : styles['accordion-bordered'],
        containerClassName || accordionGroupContext.containerClassName,
    );
    const accordionHeaderClass = cx(
        styles['accordion-header'],
        {
            [styles['accordion-header-border-bottom']]:
                isExpanded &&
                (variant || accordionGroupContext.variant) !== AccordionVariant.UNDERLINED,
        },
        {
            [styles['accordion-header-underlined']]:
                (variant || accordionGroupContext.variant) === AccordionVariant.UNDERLINED,
        },
        headerClassName || accordionGroupContext.headerClassName,
    );
    const headerArrowClass = cx(
        styles['accordion-header-arrow'],
        { [styles['accordion-header-arrow-expanded']]: isExpanded },
        headerArrowClassName || accordionGroupContext.headerArrowClassName,
    );
    const accordionBodyClass = cx(
        styles['accordion-body'],
        { [styles['accordion-body-expanded']]: isExpanded },
        bodyClassName || accordionGroupContext.bodyClassName,
    );

    function toggleExpansion(shouldExpand: boolean) {
        setIsExpanded(shouldExpand);
    }

    return (
        <div className={accordionContainerClassName}>
            <>
                <div
                    className={accordionHeaderClass}
                    onClick={() => {
                        toggleExpansion(!isExpanded);
                    }}
                >
                    {header}
                    <span className={headerArrowWrapperClassName}>
                        <ArrowDownIcon className={headerArrowClass} {...chevronIconProps} />
                    </span>
                </div>
                <div className={accordionBodyClass}>{children}</div>
            </>
        </div>
    );
};
